import { faStar } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useEffect, useState } from 'react';
import { useAuthState } from 'react-firebase-hooks/auth';
import { addRecipeReview, fetchRecipeReviews } from './api';
import { auth } from './firebase';

function ReviewForm({ recipeId, onReviewAdded }) {
  const [user] = useAuthState(auth);
  const [rating, setRating] = useState(5);
  const [reviewText, setReviewText] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!user) {
      setError('You must be logged in to leave a review');
      return;
    }

    if (rating < 1 || rating > 5) {
      setError('Rating must be between 1 and 5');
      return;
    }

    setLoading(true);
    setError('');

    try {
      await addRecipeReview(recipeId, { rating, reviewText });
      setRating(5);
      setReviewText('');
      onReviewAdded?.();
    } catch (err) {
      setError(err.message || 'Failed to add review');
    } finally {
      setLoading(false);
    }
  };

  if (!user) {
    return (
      <div className="bg-gray-50 border border-gray-200 rounded-2xl p-6 text-center">
        <p className="text-gray-600">Please log in to leave a review</p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 space-y-4">
        <h3 className="text-lg font-semibold text-gray-900">Leave a Review</h3>

        <div className="space-y-2">
          <label className="text-sm font-medium text-gray-700">Rating</label>
          <div className="flex items-center gap-2">
            <select
              value={rating}
              onChange={(e) => setRating(Number(e.target.value))}
              className="rounded-xl border border-gray-200 px-3 py-2 text-gray-800 focus:outline-none focus:ring-2 focus:ring-orange-200"
            >
              <option value={1}>1 ⭐ Poor</option>
              <option value={2}>2 ⭐ Fair</option>
              <option value={3}>3 ⭐ Good</option>
              <option value={4}>4 ⭐ Great</option>
              <option value={5}>5 ⭐ Excellent</option>
            </select>
            <div className="flex gap-1">
              {[...Array(5)].map((_, i) => (
                <FontAwesomeIcon
                  key={i}
                  icon={faStar}
                  className={i < rating ? 'text-yellow-400' : 'text-gray-300'}
                />
              ))}
            </div>
          </div>
        </div>

        <div className="space-y-2">
          <label className="text-sm font-medium text-gray-700">Your Review (Optional)</label>
          <textarea
            value={reviewText}
            onChange={(e) => setReviewText(e.target.value)}
            placeholder="Share your experience with this recipe..."
            rows={4}
            maxLength={500}
            className="w-full rounded-xl border border-gray-200 px-3 py-2 text-gray-800 focus:outline-none focus:ring-2 focus:ring-orange-200 focus:border-orange-400"
          />
          <p className="text-xs text-gray-500">{reviewText.length}/500</p>
        </div>

        {error && <p className="text-sm text-red-600">{error}</p>}

        <button
          type="submit"
          disabled={loading}
          className="w-full px-4 py-2 rounded-full bg-orange-500 text-white font-medium shadow hover:bg-orange-600 transition disabled:opacity-50"
        >
          {loading ? 'Submitting...' : 'Post Review'}
        </button>
      </div>
    </form>
  );
}

function ReviewCard({ review }) {
  const stars = [...Array(5)].map((_, i) => (
    <FontAwesomeIcon
      key={i}
      icon={faStar}
      className={i < review.rating ? 'text-yellow-400' : 'text-gray-300'}
    />
  ));

  const dateAdded = review.dateAdded?.toDate?.() || review.dateAdded;
  const formattedDate = dateAdded
    ? new Date(dateAdded).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      })
    : '';

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 space-y-3">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="font-semibold text-gray-900">{review.userName}</p>
          <p className="text-xs text-gray-500">{formattedDate}</p>
        </div>
        <div className="flex gap-1">{stars}</div>
      </div>
      {review.reviewText && <p className="text-gray-700 text-sm leading-relaxed">{review.reviewText}</p>}
    </div>
  );
}

function ReviewsSection({ recipeId }) {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadReviews = async () => {
    setLoading(true);
    try {
      const fetchedReviews = await fetchRecipeReviews(recipeId);
      setReviews(fetchedReviews);
    } catch (error) {
      console.error('Error loading reviews:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadReviews();
  }, [recipeId]);

  const avgRating =
    reviews.length > 0
      ? (reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length).toFixed(1)
      : 0;

  return (
    <div className="space-y-6">
      <ReviewForm recipeId={recipeId} onReviewAdded={loadReviews} />

      {reviews.length > 0 && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-900">
              Reviews ({reviews.length})
            </h3>
            <div className="flex items-center gap-2">
              <div className="flex gap-1">
                {[...Array(5)].map((_, i) => (
                  <FontAwesomeIcon
                    key={i}
                    icon={faStar}
                    className={i < Math.round(avgRating) ? 'text-yellow-400' : 'text-gray-300'}
                  />
                ))}
              </div>
              <span className="font-semibold text-gray-900">{avgRating}</span>
            </div>
          </div>
          <div className="space-y-3">
            {reviews.map((review) => (
              <ReviewCard key={review.id} review={review} />
            ))}
          </div>
        </div>
      )}

      {!loading && reviews.length === 0 && (
        <div className="bg-gray-50 rounded-2xl p-8 text-center">
          <p className="text-gray-600">No reviews yet. Be the first to review this recipe!</p>
        </div>
      )}
    </div>
  );
}

export { ReviewsSection as default };

// Export helper hook to get average rating
export function useAverageRating(recipeId) {
  const [avgRating, setAvgRating] = useState(0);
  const [reviewCount, setReviewCount] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchRecipeReviews(recipeId)
      .then((reviews) => {
        const count = reviews.length;
        const avg =
          count > 0
            ? (reviews.reduce((sum, r) => sum + r.rating, 0) / count).toFixed(1)
            : 0;
        setAvgRating(avg);
        setReviewCount(count);
      })
      .catch((err) => console.error(err))
      .finally(() => setLoading(false));
  }, [recipeId]);

  return { avgRating, reviewCount, loading };
}
